import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { ChevronRight, Plus, Users } from "@fleet/icons";
import { Link } from "@tanstack/solid-router";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Skeleton } from "@fleet/ui";
import { api, createApiQuery } from "@/lib/api";
import { type components, queryKeys } from "@cartero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";

export function OrganizationList() {
  const { t } = useI18n();
  const orgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  return (
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-bold tracking-tight">{t(msg`Organizations`)}</h2>
        <Button size="sm" class="gap-2">
          <Plus class="h-4 w-4" />
          {t(msg`New`)}
        </Button>
      </div>

      {/* P23 / CC-60 · I3: "no response" != "nothing here". The block is
          WRAPPED (not chained): on error `isLoading` is `false`, so a
          `when={!isLoading && !isError}` with skeleton `fallback` would leave the error
          box and the skeleton at the same time, forever. */}
      <Show when={orgs.isError}>
        <QueryErrorState
          testId="organizations-error"
          compact
          title={t(msg`Couldn't load your organizations`)}
          onRetry={() => void orgs.refetch()}
        />
      </Show>
      <Show when={!orgs.isError}>
        <Show
          when={!orgs.isLoading}
          fallback={
            <div class="space-y-4">
              <For each={Array.from({ length: 3 })}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <Show
            when={orgs.data && orgs.data.length > 0}
            fallback={
              <div class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border p-12 text-center">
                <Users class="mb-4 h-12 w-12 text-muted-foreground opacity-20" />
                <h3 class="text-lg font-semibold">{t(msg`No organizations`)}</h3>
                <p class="text-sm text-muted-foreground">
                  {t(msg`Join one or create your own to collaborate.`)}
                </p>
              </div>
            }
          >
            <div class="grid gap-4">
              <For each={orgs.data}>
                {(org) => (
                  <Link to="/organizations/$orgId" params={{ orgId: org.id }} class="group block">
                    <Card class="transition-all hover:border-primary/50 hover:shadow-md">
                      <CardContent class="flex items-center gap-4 p-4">
                        <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Users class="h-6 w-6" />
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="flex items-center gap-2">
                            <h3 class="font-bold truncate">{org.name}</h3>
                            <Badge variant="outline" class="text-[10px]">
                              @{org.slug}
                            </Badge>
                          </div>
                          <p class="text-xs text-muted-foreground">
                            {(() => {
                              const id = org.id.slice(0, 8);
                              return t(msg`ID: ${id}...`);
                            })()}
                          </p>
                        </div>
                        <ChevronRight class="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </CardContent>
                    </Card>
                  </Link>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
